import { apiListCameraTrafficVolumes } from 'apis/CameraTrafficVolume';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, registerables as registerablesJS, Tooltip } from 'chart.js';
import FanLoadingIcon from 'components/FanLoadingIcon';
import { DIALOG_ERROR_GENERAL_ERROR_MESSAGE } from 'constants/message';
import { useDialog } from 'context/DialogContext';
import moment from 'moment-timezone';
import 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { TIME_FRAME, DIRECTION_OPTIONS } from 'utils/constanst';
import CustomDatePicker from './CustomDatePicker';
import CrossSectionLinePopup from './CrossSectionLinePopup';
import './CameraTrafficVolumeChart.css';
import { useAuth } from 'context/AuthContext';
import { retrieveDirectionValue } from '../helper';
import { DEBOUNCE_TIME } from 'constants/config';

moment.updateLocale('ja', {
  week: {
    dow: 1, // First day of week is Monday
  },
  weekdaysShort: ['日', '月', '火', '水', '木', '金', '土']
});

ChartJS.register(...registerablesJS);
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend);

const TRAFFIC_VOLUME_TYPE = {
  VEHICLE: 'VEHICLE',
  HUMAN: 'HUMAN'
}

const TIME_UNIT_LABELS = {
  [TIME_FRAME.TEN_MINUTES]: '10分単位',
  [TIME_FRAME.DAY]: '1時間単位',
  [TIME_FRAME.MONTH]: '1日単位',
  [TIME_FRAME.WEEK]: '1週単位',
  [TIME_FRAME.YEAR]: '1か月単位'
};

const VEHICLE_TYPES = {
  CAR: 'car',
  TRUCK: 'truck',
  BUS: 'bus',
  BIKE: 'bike',
  OTHER_VEHICLE: 'otherVehicle'
};

const VEHICLE_TYPE_COLORS = {
  [VEHICLE_TYPES.BUS]: 'rgba(248, 244, 6, 0.6)',
  [VEHICLE_TYPES.BIKE]: 'rgba(54, 162, 235, 0.6)',
  [VEHICLE_TYPES.CAR]: 'rgba(255, 205, 86, 0.6)',
  [VEHICLE_TYPES.TRUCK]: 'rgba(75, 192, 192, 0.6)',
  [VEHICLE_TYPES.OTHER_VEHICLE]: 'rgba(153, 102, 255, 0.6)'
};

const VEHICLE_TYPE_BORDER_COLORS = {
  [VEHICLE_TYPES.BUS]: 'rgba(248, 244, 6, 1)',
  [VEHICLE_TYPES.BIKE]: 'rgba(54, 162, 235, 1)',
  [VEHICLE_TYPES.CAR]: 'rgba(255, 205, 86, 1)',
  [VEHICLE_TYPES.TRUCK]: 'rgba(75, 192, 192, 1)',
  [VEHICLE_TYPES.OTHER_VEHICLE]: 'rgba(153, 102, 255, 1)'
};

const VEHICLE_TYPE_LABELS = {
  [VEHICLE_TYPES.CAR]: '自動車',
  [VEHICLE_TYPES.BUS]: 'バス',
  [VEHICLE_TYPES.BIKE]: '二輪車',
  [VEHICLE_TYPES.TRUCK]: 'トラック',
  [VEHICLE_TYPES.OTHER_VEHICLE]: 'その他'
};

const SCENARIO_COLORS = {
  1: 'rgba(43, 13, 234, 0.6)',
  2: 'rgba(54, 162, 235, 0.6)',
  3: 'rgba(255, 205, 86, 0.6)',
  4: 'rgba(75, 192, 192, 0.6)',
  5: 'rgba(153, 102, 255, 0.6)',
  6: 'rgba(248, 1, 182, 0.6)',
  7: 'rgba(75, 156, 12, 0.6)',
  8: 'rgba(132, 1, 158, 0.6)',
  9: 'rgba(255, 159, 64, 0.6)',
  10: 'rgba(143, 103, 8, 0.6)'
};

export const SCENARIO_BORDER_COLORS = {
  1: 'rgba(43, 13, 234, 1)',
  2: 'rgba(54, 162, 235, 1)',
  3: 'rgba(255, 205, 86, 1)',
  4: 'rgba(75, 192, 192, 1)',
  5: 'rgba(153, 102, 255, 1)',
  6: 'rgba(248, 1, 182, 1)',
  7: 'rgba(75, 156, 12, 1)',
  8: 'rgba(132, 1, 158, 1)',
  9: 'rgba(255, 159, 64, 1)',
  10: 'rgba(143, 103, 8, 1)'
};


const CHART_OPTIONS_DEFAULT = {
  responsive: true,
  maintainAspectRatio: false,
  title: {
    display: false,
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
    },
  },
};

const LIST_SCENARIO = [-1, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export default function CameraTrafficVolumeChart({ sensor, noMargin, defaultToHour, onDownloadExcel }) {
  const initialDate = moment().tz('Asia/Tokyo').subtract(1, 'hour').startOf('hour').toDate();
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const [data, setData] = useState({
    labels: [],
    counts: [],
    vehicleTypeCounts: [],
    failedCounts: [],
  })
  const [chartStartDate, setChartStartDate] = useState(null);
  // State for chart data and options
  const [chartData, setChartData] = React.useState({
    labels: [],
    datasets: []
  });
  const [chartOptions, setChartOptions] = useState(CHART_OPTIONS_DEFAULT);
  const [scenario, setScenario] = useState(-1);
  const [timeUnit, setTimeUnit] = useState(
    defaultToHour ? TIME_FRAME.DAY : TIME_FRAME.TEN_MINUTES
  );
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const { error } = useDialog()
  const { currentUser } = useAuth();

  const listScenario = LIST_SCENARIO.filter((item) => item < 1 || sensor.data[`trafficVolumeScenario${item}`]);

  const getLabelByScenario = (scenario) => {
    switch (scenario) {
      case -1:
        return '自動車合計';
      case 0:
        return '歩行者合計';
      default:
        return `[${sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE ? "自動車" : "歩行者"}]${sensor.data[`trafficVolumeName${scenario}`].trim()}`
    }
  }

  const scenarioOptions = listScenario.map((item) => ({ value: item, label: getLabelByScenario(item) }))
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showCrossSectionPopup, setShowCrossSectionPopup] = useState(false);

  const getCameraDirection = () => {
    const directionValue = retrieveDirectionValue(sensor);
    return `${directionValue} 断面交通量`;  
  };

  const cameraDirection = getCameraDirection();

  useEffect(() => {
    let resizeTimeout;
    let animationTimeout;

    const handleResize = () => {
      // Clear các timeout cũ
      clearTimeout(resizeTimeout);
      clearTimeout(animationTimeout);

      // Tắt animation ngay lập tức
      setChartOptions(prevOptions => ({
        ...prevOptions,
        animation: { duration: 0 },
      }));

      // Debounce resize
      resizeTimeout = setTimeout(() => {
        setWindowWidth(window.innerWidth);

        // Bật lại animation sau khi resize xong
        animationTimeout = setTimeout(() => {
          setChartOptions(prevOptions => ({
            ...prevOptions,
            animation: { duration: 1000 },
          }));
        }, 100);
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
      clearTimeout(animationTimeout);
    };
  }, []);


  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await downloadData();
        setData(data)
        setLoading(false);
      } catch (e) {
        error(DIALOG_ERROR_GENERAL_ERROR_MESSAGE)
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, timeUnit]);

  useEffect(() => {
    const handleDisplayChart = () => {
      const { labels, counts, failedCounts, vehicleTypeCounts } = data;
      if (!labels.length || !counts.length) return;

      let datasets = [];

      // 1. LOGIC STACKING SCENARIO (cho scenario -1 và 0)
      const shouldUseScenarioStacking = scenario === -1 || scenario === 0;

      // 2. LOGIC STACKING VEHICLE TYPE (cho scenario > 0 với device mới)
      const shouldUseVehicleTypeStacking =
        scenario > 0 &&
        sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE &&
        sensor.data.isNewDevice === true;

      if (shouldUseScenarioStacking) {
        // Lấy danh sách các scenario cùng loại
        const trafficType = scenario === -1 ? TRAFFIC_VOLUME_TYPE.VEHICLE : TRAFFIC_VOLUME_TYPE.HUMAN;
        const relevantScenarios = listScenario.filter(item =>
          item > 0 && sensor.data[`trafficVolumeType${item}`] === trafficType
        );

        // Tạo một dataset cho mỗi scenario trong loại đó
        relevantScenarios.forEach(scenarioId => {
          datasets.push({
            type: 'bar',
            data: counts.map(dayCounts => dayCounts[scenarioId - 1]),
            label: sensor.data[`trafficVolumeName${scenarioId}`] || `Scenario ${scenarioId}`,
            borderWidth: 1,
            stack: 'scenarios',
            backgroundColor: (context) => {
              if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.dataIndex] && !loading) {
                return 'rgba(255, 99, 132, 0.6)';
              }
              return SCENARIO_COLORS[scenarioId];
            },
            borderColor: (context) => {
              if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.dataIndex] && !loading) {
                return 'rgba(255, 99, 132, 1)';
              }
              return SCENARIO_BORDER_COLORS[scenarioId];
            },
          });
        });
      } else if (shouldUseVehicleTypeStacking) {
        // Tạo stacked datasets cho từng loại phương tiện (chỉ dành cho thiết bị mới)
        Object.values(VEHICLE_TYPES).forEach(vehicleType => {
          datasets.push({
            type: 'bar',
            data: vehicleTypeCounts.map(dayCounts => dayCounts?.[scenario - 1]?.[vehicleType] || 0),
            label: VEHICLE_TYPE_LABELS[vehicleType],
            borderWidth: 1,
            stack: 'vehicles',
            backgroundColor: (context) => {
              if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.dataIndex]) {
                return 'rgba(255, 99, 132, 0.6)';
              }
              return VEHICLE_TYPE_COLORS[vehicleType];
            },
            borderColor: (context) => {
              if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.dataIndex]) {
                return 'rgba(255, 99, 132, 1)';
              }
              return VEHICLE_TYPE_BORDER_COLORS[vehicleType];
            },
          });
        });
      } else {
        // Trường hợp còn lại: biểu đồ đơn (scenario > 0 với thiết bị cũ)
        datasets.push({
          type: 'bar',
          data: counts.map(dayCounts => dayCounts[scenario - 1]),
          label: sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE ? '単位交通量' : '単位歩行者数',
          borderWidth: 1,
          backgroundColor: function (context) {
            if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.dataIndex]) {
              return 'rgba(255, 99, 132, 0.6)';
            }
            return SCENARIO_COLORS[scenario]
          },
          borderColor: function (context) {
            if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.dataIndex]) {
              return 'rgba(255, 99, 132, 1)';
            }
            return SCENARIO_BORDER_COLORS[scenario]
          }
        });
      }

      const chartData = {
        labels: labels,
        datasets: datasets,
      };

      const chartOptions = {
        ...CHART_OPTIONS_DEFAULT,
        scales: {
          x: {
            stacked: shouldUseScenarioStacking || shouldUseVehicleTypeStacking,
            ticks: {
              color: (context) => {
                if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.index]) {
                  return 'red';
                }
                return 'black';
              },
              font: (context) => {
                if (timeUnit === TIME_FRAME.TEN_MINUTES && failedCounts[context.index]) {
                  return { size: 12, weight: 'bold' };
                }
                return { size: 12, weight: 'normal' };
              },
            },
          },
          y: {
            stacked: shouldUseScenarioStacking || shouldUseVehicleTypeStacking,
            beginAtZero: true,
          },
        },
      }

      setChartData(chartData);
      setChartOptions(chartOptions);
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      handleDisplayChart();
    }, DEBOUNCE_TIME);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, scenario, timeUnit, loading]);


  const onDateChange = (value) => {
    setSelectedDate(prev => value ? value : prev);
  }

  const getLimitDate = (s = scenario) => {
    let subtractAmount;
    const listScenarioVehicle = listScenario.filter((item) => sensor.data[`trafficVolumeType${item}`] === TRAFFIC_VOLUME_TYPE.VEHICLE);
    const listScenarioHuman = listScenario.filter((item) => sensor.data[`trafficVolumeType${item}`] === TRAFFIC_VOLUME_TYPE.HUMAN);
    switch (s) {
      case 0:
        subtractAmount = Math.max(...listScenarioVehicle.map((item) => sensor.data[`trafficVolumeStoragePeriod${item}`]));
        break;
      case -1:
        subtractAmount = Math.max(...listScenarioHuman.map((item) => sensor.data[`trafficVolumeStoragePeriod${item}`]));
        break;
      default:
        subtractAmount = sensor.data[`trafficVolumeStoragePeriod${s}`];
        break;
    }
    return moment().subtract(subtractAmount, 'days').tz('Asia/Tokyo').startOf('day')
  }

  const downloadData = async () => {
    let startDate, startTimeQuery, endTimeQuery;
    const limitDate = getLimitDate();

    if (timeUnit === TIME_FRAME.TEN_MINUTES) {
      const startOfHour = moment(selectedDate).tz('Asia/Tokyo').startOf('hour');
      startDate = startOfHour.toDate();
      startTimeQuery = startOfHour.toISOString();
      endTimeQuery = startOfHour.clone().endOf('hour').toISOString();
    } else if (timeUnit === TIME_FRAME.DAY) {
      startDate = moment.max(moment(selectedDate).tz('Asia/Tokyo').startOf('day'), limitDate).toDate();
      startTimeQuery = startDate.toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('day').toISOString();
    } else if (timeUnit === TIME_FRAME.WEEK) {
      startDate = moment(selectedDate).tz('Asia/Tokyo').startOf('week').toDate();
      startTimeQuery = moment.max(startDate, limitDate).toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('week').toISOString();
    } else if (timeUnit === TIME_FRAME.MONTH) {
      startDate = moment(selectedDate).tz('Asia/Tokyo').startOf('month').toDate();
      startTimeQuery = moment.max(startDate, limitDate).toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('month').toISOString();
    } else if (timeUnit === TIME_FRAME.YEAR) {
      startDate = moment(selectedDate).tz('Asia/Tokyo').startOf('year').toDate();
      startTimeQuery = moment.max(startDate, limitDate).toISOString();
      endTimeQuery = moment(selectedDate).tz('Asia/Tokyo').endOf('year').toISOString();
    } else {
      throw new Error('Unsupported time unit');
    }
    setChartStartDate(startDate);
    const result = await apiListCameraTrafficVolumes(sensor.id, startTimeQuery, endTimeQuery);
    const { counts, vehicleTypeCounts, failedCounts } = calculateTrafficData(result, startDate);
    const labels = generateChartLabels(startDate);
    return { counts, vehicleTypeCounts, failedCounts, labels };
  };

  const calculateTrafficData = (result, startDate) => {
    const unitCount = {
      [TIME_FRAME.TEN_MINUTES]: 6,
      [TIME_FRAME.DAY]: 24,
      [TIME_FRAME.MONTH]: moment(startDate).tz('Asia/Tokyo').daysInMonth(),
      [TIME_FRAME.WEEK]: 7,
      [TIME_FRAME.YEAR]: 12
    }[timeUnit];

    const counts = Array.from({ length: unitCount }, () => Array(10).fill(0));

    const vehicleTypeCounts = Array.from({ length: unitCount }, () =>
      Array.from({ length: 10 }, () => Object.fromEntries(Object.values(VEHICLE_TYPES).map(type => [type, 0])))
    );

    const failedCounts = Array(unitCount).fill(false);

    result?.data?.forEach((item) => {
      const date = moment(item.timestamp).tz('Asia/Tokyo');

      let index;
      // Determine the index based on the time unit
      if (timeUnit === TIME_FRAME.TEN_MINUTES) {
        index = Math.floor(date.minute() / 10);
      } else if (timeUnit === TIME_FRAME.DAY) {
        index = date.hour();
      } else if (timeUnit === TIME_FRAME.WEEK || timeUnit === TIME_FRAME.MONTH) {
        index = Math.floor((date.valueOf() - startDate.valueOf()) / (1000 * 60 * 60 * 24));
      } else if (timeUnit === TIME_FRAME.YEAR) {
        index = date.month();
      }

      if (index >= 0 && index < unitCount) {
        // Parse the data and update counts and accumulatedCounts
        const parsedData = JSON.parse(item.data);

        // Update the failedCounts array
        failedCounts[index] = failedCounts[index] || parsedData.isFailed || false;

        for (let scenarioIndex = 1; scenarioIndex <= 10; scenarioIndex++) {
          const limitDate = getLimitDate(scenarioIndex);
          const countKey = `count_${scenarioIndex}`;
          const countValue = date.isSameOrAfter(limitDate, 'day') ? parsedData[countKey] : 0;

          if (typeof countValue === 'number' && !isNaN(countValue)) {
            counts[index][scenarioIndex - 1] += countValue
          }

          if (sensor.data[`trafficVolumeType${scenarioIndex}`] === TRAFFIC_VOLUME_TYPE.VEHICLE) {
            Object.values(VEHICLE_TYPES).forEach(vehicleType => {
              const vehicleCountKey = `count_${scenarioIndex}_${vehicleType}`;
              const vehicleCountValue = date.isSameOrAfter(limitDate, 'day') ? (parsedData[vehicleCountKey] || 0) : 0;

              if (typeof vehicleCountValue === 'number' && !isNaN(vehicleCountValue)) {
                vehicleTypeCounts[index][scenarioIndex - 1][vehicleType] += vehicleCountValue;
              }
            });
          }
        }
      }
    });

    return { counts, vehicleTypeCounts, failedCounts };
  };

  const generateChartLabels = (startDate) => {
    if (timeUnit === TIME_FRAME.TEN_MINUTES) {
      return Array.from({ length: 6 }, (_, i) =>
        moment(startDate).add(i * 10, 'minutes').format('HH:mm')
      );
    } else if (timeUnit === TIME_FRAME.DAY) {
      return Array.from({ length: 24 }, (_, i) =>
        moment(startDate).tz('Asia/Tokyo').startOf('day').add(i, 'hours').format("HH:mm"),
      );
    } else if (timeUnit === TIME_FRAME.WEEK || timeUnit === TIME_FRAME.MONTH) {
      const count = timeUnit === TIME_FRAME.WEEK ? 7 : moment(startDate).tz('Asia/Tokyo').daysInMonth();
      return Array.from({ length: count }, (_, i) => {
        const date = moment(startDate).tz('Asia/Tokyo').add(i, 'days');
        return date.format('M/DD(ddd)');
      });
    } else {
      return Array.from({ length: 12 }, (_, i) =>
        moment(startDate).tz('Asia/Tokyo').startOf('year').add(i, 'months').format('M月')
      );
    }
  };

  const generateCSVLabels = (startDate) => {
    if (timeUnit === TIME_FRAME.TEN_MINUTES || timeUnit === TIME_FRAME.DAY) {
      const count = timeUnit === TIME_FRAME.TEN_MINUTES ? 6 : 24;
      const unit = timeUnit === TIME_FRAME.TEN_MINUTES ? 'minutes' : 'hours';
      const amount = timeUnit === TIME_FRAME.TEN_MINUTES ? 10 : 1;
      const start = timeUnit === TIME_FRAME.TEN_MINUTES ? moment(startDate).tz('Asia/Tokyo') : moment(startDate).tz('Asia/Tokyo').startOf('day');
      return Array.from({ length: count }, (_, i) =>
        start.clone().add(i * amount, unit).format('YYYY/MM/DD HH:mm')
      );
    } else if (timeUnit === TIME_FRAME.WEEK || timeUnit === TIME_FRAME.MONTH) {
      const count = timeUnit === TIME_FRAME.WEEK ? 7 : moment(startDate).tz('Asia/Tokyo').daysInMonth();
      return Array.from({ length: count }, (_, i) => {
        const date = moment(startDate).tz('Asia/Tokyo').add(i, 'days');
        return date.format('YYYY/MM/DD (ddd)');
      });
    } else {
      return Array.from({ length: 12 }, (_, i) =>
        moment(startDate).tz('Asia/Tokyo').startOf('year').add(i, 'months').format('YYYY/MM')
      );
    }
  };


  function generateDataForVehicleExcel() {
    // Handle vehicle scenarios
    const numberHours = 24;
    let relevantScenarios = [];

    // If scenario is -1, get all vehicle scenarios
    if (scenario === -1) {
      relevantScenarios = listScenario.filter(item =>
        item > 0 && sensor.data[`trafficVolumeType${item}`] === TRAFFIC_VOLUME_TYPE.VEHICLE
      );
    } else relevantScenarios = [scenario];

    // Create datasets for each relevant scenario each with vehicle type breakdown
    const datasets = relevantScenarios.map(scenarioId => ({
      name: getLabelByScenario(scenarioId),
      data: Array.from({ length: numberHours }, () => ({})),
      countData: Array.from({ length: numberHours }, () => ({})),
    }));

    // Aggregate data into the datasets
    for (let index = 0; index < relevantScenarios.length; index++) {
      const scenarioId = relevantScenarios[index];
      for (let hour = 0; hour < numberHours; hour++) {
        const hourData = data.vehicleTypeCounts[hour];
        const countData = data.counts[hour];
        const scenarioData = hourData ? hourData[scenarioId - 1] : null;
        const scenarioCountData = countData ? countData[scenarioId - 1] : null;
        datasets[index].data[hour] = JSON.parse(JSON.stringify(scenarioData)); // Deep copy to avoid mutation
        datasets[index].countData[hour] = JSON.parse(JSON.stringify(scenarioCountData));
      }
    }

    return datasets;
  }

  function generateDataForPedestrianExcel() {
    // Handle pedestrian scenarios
    const numberHours = 24;
    let relevantScenarios = [];

    // If scenario is 0, get all pedestrian scenarios
    if (scenario === 0) {
      relevantScenarios = listScenario.filter(item =>
        item > 0 && sensor.data[`trafficVolumeType${item}`] === TRAFFIC_VOLUME_TYPE.HUMAN
      );
    } else relevantScenarios = [scenario];

    // Create datasets for each relevant scenario
    const datasets = relevantScenarios.map(scenarioId => ({
      name: getLabelByScenario(scenarioId),
      data: Array.from({ length: numberHours }, () => ({
        pedestrian: 0,
      })),
    }));

    // Aggregate data into the datasets
    for (let index = 0; index < relevantScenarios.length; index++) {
      const scenarioId = relevantScenarios[index];
      for (let hour = 0; hour < numberHours; hour++) {
        const hourData = data.counts[hour];
        datasets[index].data[hour].pedestrian = hourData ? hourData[scenarioId - 1] : 0;
      }
    }

    return datasets;
  }

  function generateDataForExcel(templateType) {
    let datasets = [];
    if (templateType === 'vehicle') {
      datasets = generateDataForVehicleExcel();
    } else datasets = generateDataForPedestrianExcel();

    return datasets;
  };

  const handleDownloadCSV = async () => {
    // if (!chartStartDate) return;
    // const csvLabels = generateCSVLabels(chartStartDate);
    // const { counts, vehicleTypeCounts } = data;
    // const csvData = generateCSVData(csvLabels, counts, vehicleTypeCounts, scenario);
    // generateCSV(scenario, csvData);

    // DON'T DELETE BELOW - FOR FUTURE EXCEL DOWNLOAD FEATURE
    // check if download excel
    const isDownloadExcel = onDownloadExcel && timeUnit === TIME_FRAME.DAY;
    if (isDownloadExcel) {
      let templateType = 'vehicle';
      if (scenario === 0 || sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.HUMAN)
        templateType = 'pedestrian';
      // Retrieve data for Excel
      const datasets = generateDataForExcel(templateType);
      const dateStr = moment(selectedDate).tz('Asia/Tokyo').format('YYYYMMDD');
      const filename = `${sensor.name}_${scenario === -1 ? '自動車合計' : scenario === 0 ? '歩行者合計' : sensor.data[`trafficVolumeName${scenario}`]}_${TIME_UNIT_LABELS[timeUnit]}_${dateStr}.xlsx`;
      await onDownloadExcel(templateType, datasets, filename, selectedDate);
    } else {
      if (!chartStartDate) return;
      const csvLabels = generateCSVLabels(chartStartDate);
      const { counts, vehicleTypeCounts } = data;
      const csvData = generateCSVData(csvLabels, counts, vehicleTypeCounts, scenario);
      generateCSV(scenario, csvData);
    }
  }

  const generateCSVData = (labels, counts, vehicleTypeCounts, scenario) => {
    const isVehicleScenario = sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE;
    const isTotalScenario = scenario === -1 || scenario === 0;

    if (isTotalScenario) {
      // Logic cho tổng xe (-1) và tổng người đi bộ (0)
      const relevantScenarios = listScenario.filter(item =>
        item > 0 && sensor.data[`trafficVolumeType${item}`] === (scenario === -1 ? TRAFFIC_VOLUME_TYPE.VEHICLE : TRAFFIC_VOLUME_TYPE.HUMAN)
      );

      return labels.map((label, index) => {
        const row = relevantScenarios.reduce((acc, scenarioId) => {
          acc[sensor.data[`trafficVolumeName${scenarioId}`]] = counts[index][scenarioId - 1] || 0;
          return acc;
        }, {});
        const total = Object.values(row).reduce((sum, value) => sum + value, 0);
        return { '日付': label, ...row, '合計': total };
      });
    } else if (isVehicleScenario && sensor.data.isNewDevice) {
      // Logic cho trường hợp đặc biệt: scenario là VEHICLE type và sensor.data.isNewDevice = true
      return labels.map((label, index) => {
        const row = Object.values(VEHICLE_TYPES).reduce((acc, vehicleType) => {
          acc[VEHICLE_TYPE_LABELS[vehicleType]] = vehicleTypeCounts[index][scenario - 1][vehicleType] || 0;
          return acc;
        }, {});
        const total = Object.values(row).reduce((sum, value) => sum + value, 0);
        return { '日付': label, ...row, '合計': total };
      });
    } else if (isVehicleScenario && !sensor.data.isNewDevice) {
      let cumulativeTraffic = 0;
      return labels.map((label, index) => {
        const unitTraffic = counts[index][scenario - 1] || 0;
        cumulativeTraffic += unitTraffic;
        return { '日付': label, '自動車': unitTraffic };
      });
    } else {
      // Logic cho scenario > 0 (biểu đồ đơn)
      let cumulativeTraffic = 0;
      return labels.map((label, index) => {
        const unitTraffic = counts[index][scenario - 1] || 0;
        cumulativeTraffic += unitTraffic;
        return { '日付': label, '歩行者': unitTraffic };
      });
    }
  };

  function generateCSV(scenario, data) {
    let csvContent = '\uFEFF';
    let headers = ['日付'];
    const isVehicleScenario = sensor.data[`trafficVolumeType${scenario}`] === TRAFFIC_VOLUME_TYPE.VEHICLE;
    const isTotalScenario = scenario === -1 || scenario === 0;

    if (isTotalScenario) {
      // Header cho tổng xe (-1) và tổng người đi bộ (0)
      const relevantScenarios = listScenario.filter(item =>
        item > 0 && sensor.data[`trafficVolumeType${item}`] === (scenario === -1 ? TRAFFIC_VOLUME_TYPE.VEHICLE : TRAFFIC_VOLUME_TYPE.HUMAN)
      );
      headers.push(...relevantScenarios.map(scenarioId => sensor.data[`trafficVolumeName${scenarioId}`]));
      headers.push('合計');
    } else if (isVehicleScenario && sensor.data.isNewDevice) {
      // Header cho trường hợp đặc biệt
      headers.push(...Object.values(VEHICLE_TYPES).map(vehicleType => VEHICLE_TYPE_LABELS[vehicleType]));
      headers.push('合計');
    } else if (isVehicleScenario && !sensor.data.isNewDevice) {
      headers.push('自動車');
    } else {
      // Header cho scenario > 0
      headers.push('歩行者');
    }

    csvContent += headers.join(',') + '\n';
    data.forEach(row => {
      const rowValues = headers.map(header => row[header] || 0);
      csvContent += rowValues.join(',') + '\n';
    });

    const dateStr = moment(chartStartDate).tz('Asia/Tokyo').format('YYYYMMDD');
    const filename = `${sensor.name}_${scenario === -1 ? '自動車合計' : scenario === 0 ? '歩行者合計' : sensor.data[`trafficVolumeName${scenario}`]}_${TIME_UNIT_LABELS[timeUnit]}_${dateStr}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Sub component
  const ScenarioSelector = () => (
    <select value={scenario} onChange={(e) => setScenario(parseInt(e.target.value))}>
      {scenarioOptions.map((item) => (
        <option key={item.value} value={item.value}>{item.label}</option>
      ))}
    </select>
  );

  const TimeUnitSelector = () => (
    <select value={timeUnit} onChange={(e) => setTimeUnit(e.target.value)}>
      {Object.entries(TIME_UNIT_LABELS).map(([value, label]) => (
        <option key={value} value={value}>{label}</option>
      ))}
    </select>
  );


  return (
    <div className='camera_traffic_volume_chart_container' style={noMargin ? { marginTop: 0 } : {}}>
      {loading && <FanLoadingIcon size={80} />}
      <div className={`camera_traffic_volume_chart_header ${noMargin ? 'no-center-datepicker' : ''}`} style={noMargin ? { marginTop: 0 } : {}}>
        <div className='camera_traffic_volume_chart_header_left'>
          <ScenarioSelector />
          <button onClick={() => setShowCrossSectionPopup(true)} className='button_active button_width'>
            <span>断面ライン</span>
          </button>
          <button onClick={handleDownloadCSV} className='button_active button_width'>
            <span>ダウンロード</span>
          </button>
        </div>

        <div className='camera_traffic_volume_chart_header_center'>
          <CustomDatePicker timeUnit={timeUnit} selectedDate={selectedDate} onDateChange={onDateChange} applyTransform={noMargin} />
        </div>
        <div className='camera_traffic_volume_chart_header_right'>
          <TimeUnitSelector />
        </div>
      </div>
      {cameraDirection && (
        <div className='camera_traffic_volume_chart_direction'>
          {cameraDirection}
        </div>
      )}
      <div className='camera_traffic_volume_chart_bar_container'>
        <div className='camera_traffic_volume_chart_bar'><Bar options={chartOptions} data={chartData} /></div>
      </div>
      <CrossSectionLinePopup
        isOpen={showCrossSectionPopup}
        onClose={() => setShowCrossSectionPopup(false)}
        sensor={sensor}
        accountType={currentUser?.accountType}
      />
    </div>
  )
}

CameraTrafficVolumeChart.propTypes = {
  sensor: PropTypes.object,
  noMargin: PropTypes.bool,
  defaultToHour: PropTypes.bool
}

CameraTrafficVolumeChart.defaultProps = {
  noMargin: false,
  defaultToHour: false
};
